import * as React from "react";
import {
  Box, Grid, Typography, Card, CardContent,
  TextField, Stack, Divider, Paper, InputAdornment, Button
} from "@mui/material";
import { usePricing } from "../../contexts/PricingContext";
import {useCompany} from "../../contexts/CompanyContext";
import { updateResume } from "../../services/pricingService";
import PropTypes from "prop-types";

const currency = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const parsePercent = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const cleaned = String(v).replace(/[^\d.,-]/g, "").replace(/\./g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const sumHoras = (arr) => {
  if (!Array.isArray(arr)) return "00:00";
  let totalMin = 0;
  arr.forEach(item => {
    if (item?.tempo) {
      const [h, m] = String(item.tempo).split(":").map(Number);
      totalMin += (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
    }
  });
  return totalMin;
};

const calcularCustoHora = (valor, horasMes) => {
  const s = parseFloat(valor);
  const h = parseFloat(horasMes);
  if (!s || !h || h === 0) return "";
  return (s / h).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const sumValor = (arr) => (Array.isArray(arr) ? arr : []).reduce((acc, item) => acc + toNumber(item?.valor), 0);
  
export default function PricingResume({
  stickySummary = false,
  onBackToEtapa1,
  onBackToEtapa2,
}) {

  
  const { formData } = usePricing();
  const { company } = useCompany();
  const totalCustosFixos = sumValor(formData.custosFixos || []);
  const totalCustosVariaveis = sumValor(formData.custosVariaveis || []);
  const horasEscritorio = company?.company?.tempoSemanal*4 || 0;
  const custoHoraEscritorio = calcularCustoHora(totalCustosFixos + totalCustosVariaveis, horasEscritorio);

  const valorVenda = sumValor(formData.venda);
  const horasVenda = sumHoras(formData.venda);

  const valorLevantamento = sumValor(formData.levantamento);
  const horasLevantamento = sumHoras(formData.levantamento);
  
  const valorEstudoPreliminar = sumValor(formData.estudoPreliminar);
  const horasEstudprelimiinar = sumHoras(formData.estudoPreliminar);

  const valorAnteprojeto = sumValor(formData.anteprojeto);
  const horasAnteprojeto = sumHoras(formData.anteprojeto);

  const valorProjetoLegal = sumValor(formData.projetoLegal);
  const horasProjetoLegal = sumHoras(formData.projetoLegal);

  const valorProjetoExecutivo = sumValor(formData.projetoExecutivo);
  const horasProjetoExecutivo = sumHoras(formData.projetoExecutivo);

  const valorAcompanhamentoObra = sumValor(formData.acompanhamentoObra);
  const horasAcompanhamentoObra = sumHoras(formData.acompanhamentoObra);

  const valorCustosAdicionais = sumValor(formData.custosAdicionais);

  const tempoTotal = horasVenda + horasLevantamento + horasEstudprelimiinar + horasAnteprojeto + horasProjetoLegal + horasProjetoExecutivo + horasAcompanhamentoObra;
  const totalHorasDecimais = tempoTotal / 60;
  const custoEscritorio = totalHorasDecimais * toNumber(custoHoraEscritorio);
  const custoEquipe = valorVenda + valorLevantamento + valorEstudoPreliminar + valorAnteprojeto + valorProjetoLegal + valorProjetoExecutivo + valorAcompanhamentoObra;
  const custoEscritorioEquipe = custoEquipe + custoEscritorio
  const custosAdicionais = custoEscritorioEquipe + valorCustosAdicionais;

  const [lucroPercent, setLucroPercent] = React.useState(formData.lucroPercent||25);
  const [impostoPercent, setImpostoPercent] = React.useState(formData.impostoPercent||6);
  
  const lucroValor = custosAdicionais * (1+(lucroPercent / 100));
  const impostoValor = lucroValor * (1+(impostoPercent / 100));

  const onChange = (field, value) => {
    if (field === "lucroPercent") setLucroPercent(value);
    if (field === "impostoPercent") setImpostoPercent(value);
  };

  const onSave = () => {
    formData.lucroPercent = parsePercent(lucroPercent);
    formData.impostoPercent = parsePercent(impostoPercent);
    formData.sugestedPrice = impostoValor;
    updateResume(company, formData);
  };


  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", py: 6 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          gap: 3,          
          width: "100%",
        }}
      >
        <Card sx={{ borderRadius: 3, flex: "0 1 460px", maxWidth: { md: 520 } }}>
          <CardContent>
            <Stack spacing={1.1} sx={{ color: "text.secondary" }}>
              {[
                { label: "Vendas", value: valorVenda },
                { label: "Levantamento", value: valorLevantamento },
                { label: "Estudo preliminar", value: valorEstudoPreliminar },
                { label: "Anteprojeto", value: valorAnteprojeto },
                { label: "Projeto Legal", value: valorProjetoLegal },
                { label: "Projeto Executivo", value: valorProjetoExecutivo },
                { label: "Acompanhamento de obra", value: valorAcompanhamentoObra },
                { label: "Custos Adicionais", value: valorCustosAdicionais },
              ].filter(row => row.value > 0) 
                .map((row) => (
                <Box
                  key={row.label}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography>{row.label}</Typography>

                  <Typography
                    sx={{
                      ml: 2,
                      minWidth: 120,              
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {currency.format(row.value)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }} />

        <Stack
          spacing={2}
          sx={{
            width: { xs: "100%", md: "auto" },
            alignItems: { xs: "stretch", md: "flex-end" },
          }}
        >
          <Card sx={{ width: { xs: "100%", md: 360 }, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Custo do escritório</Typography>
              <Typography variant="h5" fontWeight={800}>
                {currency.format(custoEscritorio)}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ width: { xs: "100%", md: 360 }, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Custo do escritório + equipe</Typography>
              <Typography variant="h5" fontWeight={800}>
                {currency.format(custoEscritorioEquipe)}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ width: { xs: "100%", md: 360 }, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Custo do escritório + equipe + adicionais</Typography>
              <Typography variant="h5" fontWeight={800}>
                {currency.format(custosAdicionais)}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Divider sx={{ my: 4, borderBottomWidth: 0 }} />

      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, md: 3 },
          py: 3,
          borderColor: "divider",
          position: stickySummary ? "sticky" : "static",
          bottom: stickySummary ? 0 : "auto",
          background: (t) => (stickySummary ? t.palette.background.paper : "transparent"),
          backgroundColor: "grey.200", 
        }}
      >
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ borderRadius: 3, flex: 1, display: "flex" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 160 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Qual o lucro esperado nesse projeto?</Typography>
                  <TextField
                    size="small"
                    value={String(lucroPercent)}
                    onChange={(e) => onChange("lucroPercent",e.target.value) }
                    sx={{ width: 90 }}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: {
                          inputMode: "decimal",
                          pattern: "[0-9.,]*",
                          autoComplete: "off",
                        },
                      },
                    }}
                  />
                </Stack>
                <Typography variant="h6" fontWeight={800}>{currency.format(lucroValor)}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ borderRadius: 3, flex: 1, display: "flex" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 160 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Qual o valor do imposto?</Typography>
                  <TextField
                    size="small"
                    value={String(impostoPercent)}
                    onChange={(e) => onChange("impostoPercent",e.target.value) }
                    sx={{ width: 90 }}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: {
                          inputMode: "decimal",
                          pattern: "[0-9.,]*",
                          autoComplete: "off",
                        },
                      },
                    }}
                  />
                </Stack>
                <Typography variant="h6" fontWeight={800}>{currency.format(impostoValor)}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sx={{ display: "flex" }}>
            <Card sx={{ borderRadius: 3, flex: 1, display: "flex" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 160 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Preço sugerido para o Projeto</Typography>
                </Stack>
                <Typography variant="h6" fontWeight={800}>{currency.format(impostoValor)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,          
            width: "100%",  
          }}
        >
          {(onBackToEtapa1 || onBackToEtapa2) && (
            <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
              {onBackToEtapa1 && (
                <Button variant="outlined" onClick={onBackToEtapa1}>
                  Voltar p/ Etapa 1
                </Button>
              )}
              {onBackToEtapa2 && (
                <Button variant="outlined" onClick={onBackToEtapa2}>
                  Voltar p/ Etapa 2
                </Button>
              )}
            </Stack>
          )}
          
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={!onSave}
          >
            Salvar
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
PricingResume.propTypes = {
  stickySummary: PropTypes.object.isRequired,
  onBackToEtapa1: PropTypes.func.isRequired,
  onBackToEtapa2: PropTypes.func.isRequired,
};